import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import type { Project, DemoModeState } from '@shared/types'
import { getExpiryBannerModel } from '@shared/trial-display'
import { compareProjects } from '../utils/project-list'
import { useLicense } from '../contexts/LicenseContext'
import { useApi } from '../contexts/ApiContext'
import { useGenerationContext } from '../contexts/GenerationContext'
import TrialBanner from './TrialBanner'
import LicenseExpiredModal from './LicenseExpiredModal'

export default function Layout() {
  const [allProjects, setAllProjects] = useState<Project[]>([])
  const [demoState, setDemoState] = useState<DemoModeState | null>(null)
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [sortBy, setSortBy] = useState<'name' | 'date'>('date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [nowMs, setNowMs] = useState(() => Date.now())
  const [isAdmin, setIsAdmin] = useState(false)
  const [currentUserEmail, setCurrentUserEmail] = useState('')
  const [showOnlyMine, setShowOnlyMine] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [hideDemoProjects, setHideDemoProjects] = useState(false)
  const navigate = useNavigate()
  const { status: licenseStatus } = useLicense()
  const api = useApi()
  const { generationCompletedAt, activeJobs } = useGenerationContext()
  const isLicenseExpired = licenseStatus?.isExpired && licenseStatus?.isActivated

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60 * 1000)
    return () => clearInterval(id)
  }, [])

  // Resolved once on mount; subsequent route-change loads read from the ref
  // so isAdmin never causes a second /projects fetch.
  const adminCheckedRef = useRef(false)
  const isAdminRef = useRef(false)
  isAdminRef.current = isAdmin

  // Admin sidebar needs demo datasets merged in (getAdminDashboards excludes them).
  // listProjects always includes demo datasets as the first entries.
  // isAdmin is only ever set on web (see the admin check below), so adminMode implies web.
  const fetchSidebarProjects = async (adminMode: boolean): Promise<Project[]> => {
    if (adminMode) {
      const [adminPs, userPs] = await Promise.all([api.getAdminDashboards(), api.listProjects()])
      return [...userPs.filter((p) => p.isDemo), ...adminPs]
    }
    return api.listProjects()
  }

  // projectsLoading starts true, so the initial skeleton needs no extra state change here.
  useEffect(() => {
    void (async () => {
      let useAdmin = isAdminRef.current

      if (api.platform === 'web' && !adminCheckedRef.current) {
        adminCheckedRef.current = true
        try {
          const { isAdmin: a, email } = await api.checkAdminAccess()
          useAdmin = a
          isAdminRef.current = a
          setIsAdmin(a)
          if (email) setCurrentUserEmail(email)
          else api.getSessionEmail().then(setCurrentUserEmail).catch(() => {})
        } catch {
          setIsAdmin(false)
        }
      }

      const [ps, ds] = await Promise.all([
        fetchSidebarProjects(useAdmin),
        api.getDemoState(),
      ])
      setAllProjects(ps)
      setDemoState(ds)
      setProjectsLoading(false)
      setHideDemoProjects(false)
    })()
    // Mount-only: initial sidebar load. Later refreshes go through refreshProjects().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Refresh both project list and demo state — called by child pages after changes
  const refreshProjects = async () => {
    const [ps, ds] = await Promise.all([
      fetchSidebarProjects(isAdmin),
      api.getDemoState(),
    ])
    setAllProjects(ps)
    setDemoState(ds)
  }

  // Refresh project list when a background generation completes
  useEffect(() => {
    if (!generationCompletedAt) return
    void refreshProjects()
    // Fire only when a generation completes; refreshProjects is recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationCompletedAt])

  // Optimistically update a renamed project in the sidebar without a full re-fetch
  useEffect(() => {
    const handler = (e: Event) => {
      const { id, name, client } = (e as CustomEvent<{ id: string; name: string; client: string }>).detail
      setAllProjects((prev) => prev.map((p) => p.id === id ? { ...p, name, client } : p))
    }
    window.addEventListener('tn:project-renamed', handler)
    return () => window.removeEventListener('tn:project-renamed', handler)
  }, [])

  const isDemoActive = demoState?.active ?? false

  // Filter: hide demo projects when demo mode is off, hide projects currently being generated,
  // and hide unfinished/broken dashboards. A project is considered complete when it has
  // data/counts AND every subfunction has at least one role — guards against partial
  // generations that saved a skeleton with empty roles: {}.
  // Feedback jobs are excluded — they update existing projects, not skeletons, so those
  // projects must stay visible in the sidebar while changes apply in the background.
  const PIPELINE_AGE_WINDOW_MS = 45 * 60 * 1000
  const inProgressProjectIds = new Set(
    activeJobs
      .filter((j) => j.type !== 'feedback' && (j.status === 'pending' || j.status === 'running'))
      .map((j) => j.projectId)
  )
  const baseProjects = (isDemoActive && !hideDemoProjects ? allProjects : allProjects.filter((p) => !p.isDemo))
    .filter((p) => {
      if (p.mode !== 'outside-in' && p.mode !== 'inside-out') return false
      if (inProgressProjectIds.has(p.id)) return false
      if (p.isDemo) return true
      // Recently-created projects with no data are in-flight (step pipeline or orphaned job).
      // Pass them through so Home.tsx can show them as "In Progress" skeleton cards.
      const hasData = p.data != null || (p.subfunctionCount != null && p.subfunctionCount > 0) || (p.automationBuildCount != null && p.automationBuildCount > 0)
      if (!hasData) {
        return nowMs - new Date(p.createdAt).getTime() < PIPELINE_AGE_WINDOW_MS
      }
      return !p.data?.subfunctions || p.data.subfunctions.every((sf) => Object.keys(sf.roles ?? {}).length > 0)
    })

  // Admin "mine only" toggle — pure client-side, no server round-trip
  // Mirrors listProjects() logic: own (createdBy) + shared with me (sharedWith)
  const projects = isAdmin && showOnlyMine && currentUserEmail
    ? baseProjects.filter((p) => p.isDemo || p.createdBy === currentUserEmail || p.sharedWith?.includes(currentUserEmail))
    : baseProjects

  // Note: `projects` here is the admin/demo-filtered list; search filter is applied after sorting
  const sortedProjects = [...projects].sort((a, b) => compareProjects(a, b, sortBy, sortDir))

  const filteredProjects = searchQuery.trim()
    ? sortedProjects.filter((p) => {
        const q = searchQuery.toLowerCase()
        return (
          (p.name || '').toLowerCase().includes(q) ||
          (p.client || '').toLowerCase().includes(q) ||
          String(p.industry || '').toLowerCase().includes(q)
        )
      })
    : sortedProjects

  // Update demo active state in-memory without re-fetching the project list.
  // Demo projects are always included in the initial fetch, so visibility is
  // purely a client-side concern controlled by isDemoActive + hideDemoProjects.
  const updateDemoActive = (active: boolean) => {
    setDemoState((prev) => prev ? { ...prev, active } : { active, source: 'settings' })
    if (active) setHideDemoProjects(false)
  }

  const banner = getExpiryBannerModel(licenseStatus, nowMs)

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-white border-r border-gray-100 flex flex-col shadow-sm">
        {/* Logo */}
        <div className="px-6 py-5 border-b border-gray-100 drag-region">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
              style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
            >
              TN
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900 leading-tight flex items-center gap-1.5">
                Talent Navigator
                {demoState?.active && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded">
                    Demo
                  </span>
                )}
              </div>
              <div className="text-[10px] text-gray-400 leading-tight">Workforce Reinvention</div>
            </div>
          </div>
        </div>

        {/* New Project CTA — hidden when license is expired or in demo mode */}
        {!isLicenseExpired && !isDemoActive && (
          <div className="px-4 pt-4">
            <button
              onClick={() => navigate('/new')}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
            >
              <span className="text-lg leading-none">+</span>
              New Navigator
            </button>
          </div>
        )}

        {/* Search */}
        {sortedProjects.length > 0 && (
          <div className="px-4 pt-2">
            <div className="relative">
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search talent navigators…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-6 py-1.5 text-xs rounded-lg bg-gray-50 border border-gray-200 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple-300 focus:ring-1 focus:ring-purple-100 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-base leading-none"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        )}

        {/* My Dashboards */}
        <div className="flex-1 overflow-y-auto px-4 pt-4">
          <div className="flex items-center justify-between px-2 mb-2 group">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `text-[10px] font-semibold uppercase tracking-widest ${isActive ? 'text-purple-600' : 'text-gray-400 hover:text-gray-600'}`
              }
            >
              My Talent Navigators
            </NavLink>
            {isAdmin ? (
              <button
                onClick={() => setShowOnlyMine((v) => !v)}
                title={showOnlyMine ? 'Showing your talent navigators — click to show all' : 'Showing all talent navigators — click to show yours only'}
                className="flex items-center gap-1"
              >
                <div className={`relative inline-flex h-3.5 w-6 items-center rounded-full transition-colors ${!showOnlyMine ? 'bg-purple-500' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-2.5 w-2.5 rounded-full bg-white shadow-sm transition-transform ${!showOnlyMine ? 'translate-x-3' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-[9px] text-gray-400">All</span>
              </button>
            ) : (
              <NavLink
                to="/"
                className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity text-gray-400"
              >
                View all →
              </NavLink>
            )}
          </div>
          {projectsLoading && projects.length === 0 ? (
            <div className="space-y-1 px-1 py-1">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-9 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="text-xs text-gray-400 px-2 py-4 text-center">
              No projects yet. Create your first navigator!
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-xs text-gray-400 px-2 py-4 text-center">
              No matching talent navigators.
            </div>
          ) : (
            <nav className="space-y-1">
              {filteredProjects.map((p) => (
                <NavLink
                  key={p.id}
                  to={`/dashboard/${p.id}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      isActive
                        ? 'bg-purple-50 text-purple-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                >
                  {/* Color dot from client branding */}
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: p.branding?.primaryColor || '#A100FF' }}
                  />
                  <span className="truncate">{p.name || p.client}</span>
                  {currentUserEmail && p.sharedWith?.includes(currentUserEmail) && (
                    <span className="flex-shrink-0 text-[9px] font-medium text-sky-400">Shared</span>
                  )}
                  <span className="ml-auto text-[10px] text-gray-300 flex-shrink-0">
                    {p.mode === 'outside-in' ? 'AI' : 'Data'}
                  </span>
                </NavLink>
              ))}
            </nav>
          )}
        </div>

        {/* Bottom nav */}
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-1">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive ? 'bg-purple-50 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
              }`
            }
          >
            <span>🗂️</span> My Talent Navigators
          </NavLink>
          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive ? 'bg-purple-50 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
                }`
              }
            >
              <span>🏢</span> Manage Workspace
            </NavLink>
          )}
          <NavLink
            to="/templates"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive ? 'bg-purple-50 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
              }`
            }
          >
            <span>📄</span> Templates
          </NavLink>
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive ? 'bg-purple-50 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
              }`
            }
          >
            <span>⚙️</span> Settings
          </NavLink>
          {api.platform === 'web' && (
            <button
              type="button"
              onClick={() => { window.location.href = '/auth/logout?reason=manual' }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-gray-500 hover:bg-gray-50 transition-all"
            >
              <span>🚪</span> Sign out
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <TrialBanner banner={banner} />
        <main className="flex-1 overflow-y-auto">
          <Outlet context={{ refreshProjects, updateDemoActive, projects: filteredProjects, isDemoActive, projectsLoading, sortBy, setSortBy, sortDir, setSortDir, currentUserEmail, isAdmin, showOnlyMine, searchQuery, setSearchQuery, hideDemoProjects, setHideDemoProjects }} />
        </main>
      </div>

      {/* License expired modal overlay */}
      {licenseStatus && <LicenseExpiredModal status={licenseStatus} />}
    </div>
  )
}
